/* eslint-disable */

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  getSchoolSetupStatus,
  saveSchoolProfileSetup,
  saveSchoolBrandingSetup,
  updateSchoolSetupStatus,
} from "../../services/setupService";

const STEPS = [
  { key: "profileCompleted", label: "Profile" },
  { key: "brandingCompleted", label: "Branding" },
  { key: "setupCompleted", label: "Finish" },
];

const EMPTY_STATUS = {
  profileCompleted: false,
  brandingCompleted: false,
  setupCompleted: false,
};

export default function SchoolSetupWizard() {
  const [params] = useSearchParams();
  const schoolId = params.get("schoolId");

  const [step, setStep] = useState(0);
  const [status, setStatus] = useState(EMPTY_STATUS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const [schoolName, setSchoolName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#4F46E5");
  const [logoUrl, setLogoUrl] = useState("");
  const [parentAppName, setParentAppName] = useState("");

  const progress = useMemo(() => {
    const completed = STEPS.filter((item) => status[item.key]).length;
    return Math.round((completed / STEPS.length) * 100);
  }, [status]);

  useEffect(() => {
    async function loadStatus() {
      try {
        setLoading(true);

        if (!schoolId) {
          setMessage("Missing school ID.");
          return;
        }

        const saved = await getSchoolSetupStatus(schoolId);
        setStatus({ ...EMPTY_STATUS, ...saved });
      } catch (err) {
        console.error(err);
        setMessage(err?.message || "Could not load setup progress.");
      } finally {
        setLoading(false);
      }
    }

    loadStatus();
  }, [schoolId]);

  async function saveProfile() {
    if (!schoolName.trim()) {
      setMessage("School name is required.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      await saveSchoolProfileSetup({
        schoolId,
        schoolName: schoolName.trim(),
        displayName: displayName.trim() || schoolName.trim(),
        primaryColor,
      });

      setStatus((prev) => ({ ...prev, profileCompleted: true }));
      setStep(1);
      setMessage("School profile saved.");
    } catch (err) {
      console.error(err);
      setMessage(err?.message || "Could not save school profile.");
    } finally {
      setSaving(false);
    }
  }

  async function saveBranding() {
    try {
      setSaving(true);
      setMessage("");

      await saveSchoolBrandingSetup({
        schoolId,
        primaryColor,
        logoUrl,
        parentAppName,
      });

      setStatus((prev) => ({ ...prev, brandingCompleted: true }));
      setStep(2);
      setMessage("School branding saved.");
    } catch (err) {
      console.error(err);
      setMessage(err?.message || "Could not save branding.");
    } finally {
      setSaving(false);
    }
  }

  async function finishSetup() {
    try {
      setSaving(true);
      setMessage("");

      await updateSchoolSetupStatus(schoolId, {
        setupCompleted: true,
        completedAt: new Date().toISOString(),
      });

      setStatus((prev) => ({ ...prev, setupCompleted: true }));
      setMessage("Setup completed. The school admin can now continue inside the admin dashboard.");
    } catch (err) {
      console.error(err);
      setMessage(err?.message || "Could not finish setup.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>Loading setup...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.badge}>Agreement accepted</div>

        <h1 style={styles.title}>Let’s set up your school.</h1>
        <p style={styles.text}>
          Complete the school profile and branding. Staff, classes, pupils, and parents are managed by the school admin inside the main admin dashboard.
        </p>

        {message ? <div style={styles.message}>{message}</div> : null}

        <div style={styles.stepRow}>
          {STEPS.map((item, index) => (
            <button
              key={item.key}
              type="button"
              style={{
                ...styles.stepPill,
                ...(index === step ? styles.stepPillActive : null),
                ...(status[item.key] ? styles.stepPillDone : null),
              }}
              onClick={() => setStep(index)}
            >
              {status[item.key] ? "✓ " : ""}
              {item.label}
            </button>
          ))}
        </div>

        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${progress}%` }} />
        </div>

        <div style={styles.panel}>
          {step === 0 ? (
            <>
              <h2 style={styles.sectionTitle}>Confirm school profile</h2>

              <Field label="School name">
                <input style={styles.input} value={schoolName} onChange={(e) => setSchoolName(e.target.value)} />
              </Field>

              <Field label="Display name">
                <input style={styles.input} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </Field>

              <button style={styles.primaryBtn} onClick={saveProfile} disabled={saving}>
                {saving ? "Saving..." : "Save School Profile"}
              </button>
            </>
          ) : null}

          {step === 1 ? (
            <>
              <h2 style={styles.sectionTitle}>School branding</h2>

              <Field label="Primary colour">
                <input style={styles.input} value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
              </Field>

              <Field label="Logo URL">
                <input style={styles.input} value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} />
              </Field>

              <Field label="Parent app name">
                <input style={styles.input} value={parentAppName} onChange={(e) => setParentAppName(e.target.value)} />
              </Field>

              <button style={styles.primaryBtn} onClick={saveBranding} disabled={saving}>
                {saving ? "Saving..." : "Save Branding"}
              </button>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <h2 style={styles.sectionTitle}>Finish setup</h2>
              <p style={styles.bodyText}>
                The agreement has been accepted and the school profile/branding setup can now be completed. Operational data stays inside the school admin dashboard.
              </p>

              <button style={styles.primaryBtn} onClick={finishSetup} disabled={saving}>
                {saving ? "Finishing..." : "Complete Setup"}
              </button>
            </>
          ) : null}
        </div>

        <div style={styles.footer}>
          <button style={styles.secondaryBtn} disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            Back
          </button>

          <button style={styles.secondaryBtn} disabled={step === STEPS.length - 1} onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>
            Next
          </button>
        </div>

        {schoolId ? <div style={styles.schoolId}>School ID: {schoolId}</div> : null}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>
      {children}
    </label>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#eef5ff 0%,#f8fbff 55%,#f3e8ff 100%)",
    display: "grid",
    placeItems: "center",
    padding: 24,
  },
  card: {
    width: "100%",
    maxWidth: 860,
    borderRadius: 34,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    boxShadow: "0 28px 90px rgba(15,23,42,0.12)",
    padding: 42,
  },
  badge: {
    display: "inline-flex",
    borderRadius: 999,
    padding: "10px 14px",
    background: "#ecfdf3",
    color: "#166534",
    fontWeight: 950,
    marginBottom: 18,
  },
  title: {
    margin: 0,
    fontSize: 46,
    lineHeight: 1,
    letterSpacing: "-0.06em",
    color: "#0f172a",
    fontWeight: 950,
  },
  text: {
    color: "#475569",
    lineHeight: 1.7,
  },
  bodyText: {
    color: "#475569",
    lineHeight: 1.7,
    margin: 0,
  },
  message: {
    marginTop: 18,
    borderRadius: 18,
    padding: 14,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1e40af",
    fontWeight: 850,
  },
  stepRow: {
    marginTop: 24,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  stepPill: {
    borderRadius: 999,
    border: "1px solid #dbe7ff",
    background: "#f8fbff",
    color: "#64748b",
    padding: "9px 12px",
    fontSize: 12,
    fontWeight: 950,
    cursor: "pointer",
  },
  stepPillActive: {
    background: "#2563eb",
    color: "#ffffff",
    borderColor: "#2563eb",
  },
  stepPillDone: {
    background: "#ecfdf3",
    color: "#166534",
    borderColor: "#bbf7d0",
  },
  progressTrack: {
    marginTop: 14,
    height: 9,
    background: "#eef2ff",
    borderRadius: 999,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(135deg,#2563eb 0%,#7c3aed 100%)",
  },
  panel: {
    marginTop: 28,
    borderRadius: 28,
    background: "#f8fbff",
    border: "1px solid #dbeafe",
    padding: 28,
    display: "grid",
    gap: 16,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 32,
    fontWeight: 950,
    letterSpacing: "-0.05em",
    color: "#0f172a",
  },
  field: {
    display: "grid",
    gap: 8,
  },
  label: {
    fontWeight: 900,
    color: "#0f172a",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 18,
    border: "1px solid #cfe0ff",
    background: "#ffffff",
    padding: "15px 16px",
    fontSize: 15,
    color: "#0f172a",
    outline: "none",
  },
  footer: {
    marginTop: 24,
    display: "flex",
    justifyContent: "space-between",
  },
  primaryBtn: {
    border: "none",
    borderRadius: 18,
    padding: "15px 22px",
    background: "linear-gradient(135deg,#2563eb 0%,#7c3aed 100%)",
    color: "#fff",
    fontWeight: 950,
    cursor: "pointer",
  },
  secondaryBtn: {
    border: "1px solid #dbe7ff",
    borderRadius: 18,
    padding: "15px 22px",
    background: "#ffffff",
    color: "#334155",
    fontWeight: 950,
    cursor: "pointer",
  },
  schoolId: {
    marginTop: 24,
    color: "#64748b",
    fontWeight: 850,
    fontSize: 13,
  },
};